import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useToast } from "@chakra-ui/react";
import { FiImage, FiRefreshCw, FiTrash2, FiUpload } from "react-icons/fi";

const ProfileAvatarsManager = () => {
  const [avatars, setAvatars] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedGender, setSelectedGender] = useState("male");
  const fileInputRef = useRef(null);
  const toast = useToast();

  const loadAvatars = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get("/api/v2/avtar/admin");
      setAvatars(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error("Failed to load profile avatars:", error);
      toast({
        title: "Unable to load profile avatars",
        description: error.response?.data?.message || "Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      setAvatars([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAvatars();
  // Loading only when the isolated manager mounts prevents unrelated Store tabs from refetching.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (files.length !== imageFiles.length) {
      toast({
        title: "Some files were skipped",
        description: "Only image files can be uploaded as profile avatars.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
    }
    if (imageFiles.length === 0) return;

    const formData = new FormData();
    imageFiles.slice(0, 10).forEach((file) => formData.append("images", file));
    formData.append("gender", selectedGender);

    try {
      setIsUploading(true);
      await axios.post("/api/v2/avtar", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast({
        title: "Profile avatars uploaded",
        description: `${imageFiles.slice(0, 10).length} avatar(s) added to the ${selectedGender} catalogue.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      await loadAvatars();
    } catch (error) {
      console.error("Failed to upload profile avatars:", error);
      toast({
        title: "Avatar upload failed",
        description: error.response?.data?.message || "Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
      event.target.value = "";
    }
  };

  const visibleAvatars = avatars.filter((avatar) => avatar.gender === selectedGender);

  const handleDelete = async (avatar) => {
    if (!window.confirm("Delete this profile avatar?")) return;

    try {
      await axios.delete(`/api/v2/avtar/${avatar.id}`);
      setAvatars((current) => current.filter((item) => item.id !== avatar.id));
      toast({ title: "Profile avatar deleted", status: "success", duration: 3000, isClosable: true });
    } catch (error) {
      console.error("Failed to delete profile avatar:", error);
      toast({
        title: "Unable to delete profile avatar",
        description: error.response?.data?.message || "Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <section className="space-y-6" aria-labelledby="profile-avatars-heading">
      <div className="flex flex-col gap-4 rounded-2xl border-2 border-[var(--yellow)]/20 bg-gradient-to-br from-gray-900 via-gray-800 to-black p-5 shadow-lg sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 id="profile-avatars-heading" className="text-2xl font-bold text-[var(--yellow)]">Profile Avatars</h2>
          <p className="mt-1 text-sm text-[color:var(--yellow)/0.8]">Manage separate avatar catalogues for male and female users.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button type="button" onClick={loadAvatars} disabled={isLoading || isUploading} className="flex items-center gap-2 rounded-md border border-[var(--yellow)]/40 px-4 py-2 text-sm font-medium text-[var(--yellow)] transition hover:bg-[var(--yellow)]/10 disabled:cursor-not-allowed disabled:opacity-50">
            <FiRefreshCw className={isLoading ? "animate-spin" : ""} /> Refresh
          </button>
          <button type="button" onClick={() => fileInputRef.current?.click()} disabled={isUploading} className="flex items-center gap-2 rounded-md bg-[var(--yellow)] px-4 py-2 text-sm font-medium text-[var(--black)] transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50">
            <FiUpload /> {isUploading ? "Uploading..." : `Upload ${selectedGender} avatars`}
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} />
        </div>
      </div>

      <div className="flex rounded-xl border border-gray-700 bg-gray-900 p-1" role="tablist" aria-label="Avatar gender catalogue">
        {["male", "female"].map((gender) => (
          <button
            key={gender}
            type="button"
            role="tab"
            aria-selected={selectedGender === gender}
            onClick={() => setSelectedGender(gender)}
            className={`flex-1 rounded-lg px-4 py-2 text-sm font-semibold capitalize transition ${selectedGender === gender ? "bg-[var(--yellow)] text-[var(--black)]" : "text-gray-300 hover:bg-white/10"}`}
          >
            {gender} ({avatars.filter((avatar) => avatar.gender === gender).length})
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex min-h-64 items-center justify-center rounded-2xl border-2 border-gray-700 bg-gray-900">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-[var(--yellow)]/25 border-t-[var(--yellow)]" />
        </div>
      ) : visibleAvatars.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-gray-700 bg-gray-900 p-12 text-center">
          <FiImage className="mx-auto mb-4 text-4xl text-[var(--yellow)]" />
          <h3 className="text-lg font-semibold text-white">No {selectedGender} avatars yet</h3>
          <p className="mt-1 text-sm text-gray-400">Upload one or more images to make them available to {selectedGender} users.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
          {visibleAvatars.map((avatar, index) => (
            <article key={avatar.id} className="group relative overflow-hidden rounded-2xl border-2 border-[var(--yellow)]/20 bg-gray-900 shadow-lg">
              <img src={avatar.url} alt={`Profile avatar ${index + 1}`} className="aspect-square w-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-black/80 p-2 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                <span className="text-xs font-medium text-white">Avatar {index + 1}</span>
                <button type="button" onClick={() => handleDelete(avatar)} className="rounded p-1.5 text-red-300 transition hover:bg-red-500/20 hover:text-red-100" aria-label={`Delete profile avatar ${index + 1}`}>
                  <FiTrash2 />
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

export default ProfileAvatarsManager;
